import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useRecentlyViewedProductsQuery } from '@src/queries/RecentlyViewedProductsQuery.generated'
import { AnalyticsDispType } from '@src/types/graphql-types'
import { makeStyles } from '@material-ui/core/styles'
import { Theme } from '@material-ui/core'
import DynamicProductCarousel from '@src/components/DynamicProductCarousel'
import { CarouselProductTypes } from '../DynamicProductCarousel/DynamicProductCardTypes'
import clsx from 'clsx'
import { sendEmptyRecommendationGA4Event } from '@utils/analytics'
import { ErpType } from '@utils/useProductErpType'
import { useCurrentUser } from '@utils/useCurrentUser'
import { useRouter } from '@src/routes'

export const useStyles = makeStyles((theme: Theme) => {
  return {
    recentlyViewedContainer: {
      borderTop: `1px solid ${theme.palette.grey[300]}`,
      paddingTop: theme.spacing(2),
      [theme.breakpoints.down('sm')]: {
        paddingBottom: theme.spacing(14),
      },
    },
  }
})

interface RecentlyViewedProductsCarouselProps {
  dispType: AnalyticsDispType
  className?: string
  productKey?: string
  brand?: string
  searchKey?: string
  analyticsPageName: string
  analyticsPageDetails: string
  slidesToShow?: number
  setShouldDisplay?: Dispatch<SetStateAction<boolean>>
  condensed?: boolean
  productNumber?: string
  showPdpRedesignLevelTwo?: boolean
}

const RecentlyViewedProductsCarousel: React.FC<
  RecentlyViewedProductsCarouselProps
> = ({
  dispType,
  className,
  productKey = '',
  brand = '',
  searchKey = '',
  analyticsPageName,
  analyticsPageDetails,
  slidesToShow,
  setShouldDisplay,
  condensed = false,
  productNumber,
  showPdpRedesignLevelTwo,
}) => {
  const classes = useStyles()
  const router = useRouter()
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  const gaEmptyRecoSectionMap = {
    pdp: 'pdp load',
    srp: 'srp load',
    'document search': 'doc search load',
    'advanced search': 'adv search load',
  } as const

  const getGAEmptyRecoDetail = () => {
    switch (analyticsPageName.toLowerCase()) {
      case 'pdp':
        return productKey.toLowerCase()
      case 'srp':
        return searchKey.toLowerCase()
      case 'document search':
        return router.query.tab || 'sds' // the default tab for the doc search page
      default:
        return
    }
  }

  const { isBlueB2BUser } = useCurrentUser()
  const { data, loading } = useRecentlyViewedProductsQuery({
    variables: {
      dispType,
      productKey,
      brand,
      ...(isBlueB2BUser ? { erpType: ErpType.Blue } : {}),
    },
    ssr: false,
    fetchPolicy: 'no-cache',
    skip: !isMounted,
    onCompleted: (loadedData) => {
      if (
        gaEmptyRecoSectionMap[analyticsPageName.toLowerCase()] &&
        (!loadedData ||
          loadedData?.getRecentlyViewedProducts?.products.length === 0)
      ) {
        sendEmptyRecommendationGA4Event({
          action: gaEmptyRecoSectionMap[analyticsPageName.toLowerCase()],
          detail: getGAEmptyRecoDetail(),
          section: 'recently viewed products',
          productId: productNumber?.toLowerCase(),
          productBrand: brand.toLowerCase(),
        })
      }
    },
  })

  const recentlyViewProducts = data?.getRecentlyViewedProducts?.products || []
  const shouldRender = recentlyViewProducts.length > 0

  useEffect(() => {
    if (setShouldDisplay) {
      setShouldDisplay(shouldRender)
    }
  }, [shouldRender, loading])

  // Return null if no recently viewed products are found
  if (loading) return null

  return shouldRender ? (
    <div className={clsx(className, classes.recentlyViewedContainer)}>
      <DynamicProductCarousel
        type={CarouselProductTypes.RecentlyViewed}
        products={recentlyViewProducts}
        analyticsPageName={analyticsPageName}
        analyticsPageDetails={analyticsPageDetails}
        slidesToShow={slidesToShow}
        condensed={condensed}
        showPdpRedesignLevelTwo={showPdpRedesignLevelTwo}
      />
    </div>
  ) : null
}

export default RecentlyViewedProductsCarousel
